
export interface ContentItem {
  id: string;
  title: string;
  description: string;
  type: 'video' | 'pdf' | 'audio' | 'text';
  accessLevel: string;
  duration?: string;
  url: string;
  thumbnailUrl?: string;
}

export interface CourseModule {
  id: string;
  title: string;
  description: string;
  items: ContentItem[];
  module_id: string;
  accessLevel: string;
}

// Course content data organized by modules
export const courseModules = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Learn the fundamentals and get ready for success',
    accessLevel: 'launch-guide',
    items: [
      {
        id: 'intro-video',
        title: 'Introduction to AI App Development',
        description: 'An overview of what you\'ll learn and how to get the most out of this program',
        type: 'video',
        accessLevel: 'launch-guide',
        duration: '15 min',
        url: '/content/videos/introduction.mp4',
        thumbnailUrl: '/content/thumbnails/introduction.jpg'
      },
      {
        id: 'setup-guide',
        title: 'AI Development Setup Guide',
        description: 'Step-by-step instructions to prepare for the course',
        type: 'pdf',
        accessLevel: 'launch-guide',
        url: '/content/pdfs/setup-guide.pdf'
      }
    ]
  },
  {
    id: 'ai-app-development',
    title: 'AI Powered App Development Course',
    description: 'Master the essential AI app development principles and techniques',
    accessLevel: 'launch-guide',
    items: [
      {
        id: 'concept1',
        title: 'AI App Ideation & Validation',
        description: 'Learn how to generate and validate profitable app ideas using AI',
        type: 'video',
        accessLevel: 'launch-guide',
        duration: '25 min',
        url: '/content/videos/core-concept-1.mp4',
        thumbnailUrl: '/content/thumbnails/concept1.jpg'
      },
      {
        id: 'concept2',
        title: 'AI-Powered App Design',
        description: 'Using AI tools to create professional app interfaces and user experiences',
        type: 'video',
        accessLevel: 'launch-guide',
        duration: '30 min',
        url: '/content/videos/core-concept-2.mp4',
        thumbnailUrl: '/content/thumbnails/concept2.jpg'
      },
      {
        id: 'worksheet1',
        title: 'App Development Worksheet',
        description: 'Apply what you\'ve learned with practical exercises',
        type: 'pdf',
        accessLevel: 'launch-guide',
        url: '/content/pdfs/worksheet1.pdf'
      }
    ]
  },
  {
    id: 'ai-swipe-file',
    title: 'BONUS: AI Swipe File and 100 Fool Proof Prompts',
    description: 'Ready-to-use scripts and prompts for AI-powered app development',
    accessLevel: 'ai-prompts',
    items: [
      {
        id: 'swipe-file',
        title: 'AI App Development Swipe File',
        description: 'Copy-and-paste scripts to tell AI exactly how to design and code your app',
        type: 'pdf',
        accessLevel: 'ai-prompts',
        url: '/content/pdfs/swipe-file.pdf'
      },
      {
        id: 'prompts',
        title: '100 Fool Proof AI Prompts',
        description: 'Optimized prompts to make your app completely custom',
        type: 'pdf',
        accessLevel: 'ai-prompts',
        url: '/content/pdfs/100-prompts.pdf'
      }
    ]
  },
  {
    id: 'fast-track',
    title: 'AI App Fast Track',
    description: 'Build your first AI app in a weekend with our quick-start resources',
    accessLevel: 'fast-track',
    items: [
      {
        id: 'fast-track-masterclass',
        title: 'Weekend App Development Masterclass',
        description: 'Complete guide to building your app in just 48 hours',
        type: 'video',
        accessLevel: 'fast-track',
        duration: '45 min',
        url: '/content/videos/fast-track.mp4',
        thumbnailUrl: '/content/thumbnails/fast-track.jpg'
      },
      {
        id: 'fast-track-templates',
        title: 'Ready-To-Use App Templates',
        description: 'Pre-built templates to accelerate your development process',
        type: 'pdf',
        accessLevel: 'fast-track',
        url: '/content/pdfs/templates.pdf'
      }
    ]
  },
  {
    id: 'accelerator',
    title: 'AI App Accelerator',
    description: 'Learn how to launch, grow, and profit from your AI app',
    accessLevel: 'accelerator',
    items: [
      {
        id: 'monetization-masterclass',
        title: 'App Monetization Masterclass',
        description: 'Advanced strategies to maximize revenue from your app',
        type: 'video',
        accessLevel: 'accelerator',
        duration: '60 min',
        url: '/content/videos/monetization.mp4',
        thumbnailUrl: '/content/thumbnails/monetization.jpg'
      },
      {
        id: 'growth-automation',
        title: 'Growth Automation Playbook',
        description: 'AI-powered strategies for user acquisition and retention',
        type: 'pdf',
        accessLevel: 'accelerator',
        url: '/content/pdfs/growth-automation.pdf'
      }
    ]
  },
  {
    id: 'business-mastery',
    title: 'AI App Business Mastery',
    description: 'Build a profitable app business in 90 days with advanced AI techniques',
    accessLevel: 'business-mastery',
    items: [
      {
        id: 'business-masterplan',
        title: '90-Day App Business Masterplan',
        description: 'Complete roadmap to building your app business from scratch',
        type: 'video',
        accessLevel: 'business-mastery',
        duration: '75 min',
        url: '/content/videos/business-masterplan.mp4',
        thumbnailUrl: '/content/thumbnails/business.jpg'
      },
      {
        id: 'scaling-blueprint',
        title: 'App Business Scaling Blueprint',
        description: 'Strategic framework for growing your app business to 7-figures',
        type: 'pdf',
        accessLevel: 'business-mastery',
        url: '/content/pdfs/scaling-blueprint.pdf'
      }
    ]
  },
  {
    id: 'unlimited-development',
    title: 'Unlimited App Development Subscription',
    description: 'Resources for subscribers with unlimited app development access',
    accessLevel: 'unlimited',
    items: [
      {
        id: 'monthly-strategy',
        title: 'Monthly Strategy Session Recording',
        description: 'Latest trends and strategies in AI app development',
        type: 'video',
        accessLevel: 'unlimited',
        duration: '60 min',
        url: '/content/videos/monthly-strategy.mp4',
        thumbnailUrl: '/content/thumbnails/strategy.jpg'
      },
      {
        id: 'development-guides',
        title: 'Advanced Development Guides',
        description: 'In-depth technical documentation for app development',
        type: 'pdf',
        accessLevel: 'unlimited',
        url: '/content/pdfs/advanced-guides.pdf'
      }
    ]
  },
  {
    id: 'elite-vip',
    title: 'AI App Elite VIP',
    description: 'Exclusive resources for Elite VIP clients',
    accessLevel: 'elite-vip',
    items: [
      {
        id: 'vip-strategy',
        title: 'VIP Strategy Session',
        description: 'One-on-one consultation recording with action plan',
        type: 'video',
        accessLevel: 'elite-vip',
        duration: '90 min',
        url: '/content/videos/vip-strategy.mp4',
        thumbnailUrl: '/content/thumbnails/vip.jpg'
      },
      {
        id: 'custom-implementation',
        title: 'Custom Implementation Documents',
        description: 'Tailored documents for your specific app implementation',
        type: 'pdf',
        accessLevel: 'elite-vip',
        url: '/content/pdfs/custom-implementation.pdf'
      }
    ]
  }
];

// Helper function to get content by access level
export const getContentByAccessLevel = (accessLevel: string): CourseModule[] => {
  // This implements a cascading access model where higher tiers get access to all lower tier content
  const accessLevels: { [key: string]: string[] } = {
    'launch-guide': ['launch-guide'],
    'ai-prompts': ['ai-prompts'],
    'fast-track': ['launch-guide', 'fast-track'],
    'accelerator': ['launch-guide', 'accelerator'],
    'business-mastery': ['launch-guide', 'business-mastery'],
    'unlimited': ['launch-guide', 'ai-prompts', 'fast-track', 'accelerator', 'business-mastery', 'unlimited'],
    'elite-vip': ['launch-guide', 'ai-prompts', 'fast-track', 'accelerator', 'business-mastery', 'unlimited', 'elite-vip']
  };
  
  const allowedLevels = accessLevels[accessLevel] || [];
  
  return courseModules.filter(module => 
    allowedLevels.includes(module.accessLevel)
  );
};

// Helper function to get a specific content item by ID
export const getContentItemById = (itemId: string): ContentItem | undefined => {
  for (const module of courseModules) {
    const item = module.items.find(item => item.id === itemId);
    if (item) return item;
  }
  return undefined;
};
